import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";

import { BSBookInfo, BSBookInfoByMonth, BOOK_TAGS, getTagConfig } from "sketon/service/data/BSBookInfo";
import {
  useLoadBooklist,
  useLoadYearBookList,
} from "sketon/service/storage/booklistLoader";

import { isEmpty } from "sketon/utility/StringUtils";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { SCREEN_SM } from "sketon/ui/theme";
import { getNavPath } from "sketon/app/RouterConfig";
import { BSDetailPage } from "../details";
import BSBannerView from "sketon/ui/component/BannerView";
import BookListSkeleton from "sketon/ui/component/Skeleton/BookListSkeleton";
import BookCover from "sketon/ui/component/BookCover";
import { BSMainPage } from ".";
import { NotFoundPage } from "sketon/ui/404";
import { NotDataView } from "sketon/ui/404/notDataView";
import Footer from "sketon/ui/component/Footer";
import { useIsInSMSize } from "sketon/utility/DeviceInfo";
import { toCoverProxyPath } from "sketon/const";

const SytledWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  min-height: 100vh;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0rem;
  }

  position: relative;
`;

const StyledContainerDiv = styled.div`
  width: 1200px;
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;
  margin-bottom: 80px;
  min-height: 100vh;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    margin-bottom: 0;
  }
`;

const StyledSectionGroupDiv = styled.div`
  width: 100%;

  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  height: 100px;

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    padding: 0 0rem;
    height: 50px;
  }

  position: relative;
`;

const StyledTitleDiv = styled.div`
  width: 100%;
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  font-size: 44px;
  line-height: 60px;
  color: "rgba(255, 255, 255, 0.8)";
  text-align: center;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.44rem;
    line-height: 60px;
  }
`;

const StyledBackDiv = styled.div`
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  font-size: 30px;
  line-height: 100px;
  color: rgba(17, 17, 17, 0.6);
  text-align: left;
  font-weight: normal;

  position: absolute;
  left: 0.2rem;
  top: 0;
  bottom: 0;
  cursor: pointer;

  display: flex;
  align-items: center;

  &:hover {
    color: rgba(17, 17, 17, 0.9);
  }

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.3rem;
    line-height: 50px;
  }
`;

const StyledMainDiv = styled.div`
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  font-size: 30px;
  line-height: 100px;
  color: "rgba(255, 255, 255, 0.4)";
  text-align: left;
  font-weight: normal;

  position: absolute;
  right: 0.2rem;
  top: 0;
  bottom: 0;
  cursor: pointer;

  display: flex;
  align-items: center;

  &:hover {
    transform: scale(0.9);
  }

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.3rem;
    line-height: 50px;
  }
`;

const StyledNotFoundDiv = styled.div`
  width: 100%;
  height: calc(100% - 200px);

  @media (max-width: ${SCREEN_SM}) {
    height: calc(100% - 1rem);
  }
`;

const StyledTagFilterBar = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 0 20px;

  @media (max-width: ${SCREEN_SM}) {
    padding: 0 0.2rem 10px;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledTagChip = styled.span<{ $active: boolean; $color?: string; $bgColor?: string; $isAll?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  white-space: nowrap;

  ${(props) => {
    if (props.$isAll) {
      return props.$active
        ? `background: #3A2E22; color: #FBF1DC; border: 1px solid #3A2E22;`
        : `background: #F3E5C8; color: #6E5D49; border: 1px solid transparent;`;
    }
    if (props.$active) {
      return `background: ${props.$color ?? "#B0512B"}; color: #FBF1DC; border: 1px solid ${props.$color ?? "#B0512B"};`;
    }
    return `background: ${props.$bgColor ?? "#F6E7D2"}; color: ${props.$color ?? "#B0512B"}; border: 1px solid transparent;`;
  }}

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.24rem;
    padding: 2px 0.16rem;
  }
`;

const StyledListItemDiv = styled.div`
  width: 100%;
  padding: 0px 0px 20px;

  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;

  flex-wrap: wrap;

  background: #FFFFFF;
  border: 1px solid #EBD9B0;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(58, 46, 34, 0.06);

  margin-bottom: 0px;

  @media (max-width: ${SCREEN_SM}) {
    justify-content: space-between;
    width: 100%;
    padding: 0.1rem 0.4rem;
    border-radius: 0px;
  }
`;

const StyledBookItem = styled.div`
  width: fit-content;
  padding: 20px 0px 0px 20px;
  min-height: 155px;
  margin-top: 0;
  margin-left: 0;

  display: flex;
  flex-flow: row;
  justify-content: flex-start;
  align-items: flex-start;

  cursor: pointer;
  transition: 0.2s;

  :hover {
    transform: translateX(10px);
  }
  :active {
    transform: translateX(10px);
  }

  @media (min-width: ${SCREEN_SM}) {
  }

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    justify-content: flex-start;
    padding: 0.24rem 0;
    min-height: 0;

    &:not(:last-child) {
      border-bottom: 1px solid #f0e4c8;
    }
  }
`;

const StyledBookRight = styled.div`
  margin-top: 0;
  margin-left: 20px;
  padding: 0 0 0 0;
  border-radius: 0;

  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: left;
  position: relative;

  @media (min-width: ${SCREEN_SM}) {
    width: 350px;
  }

  @media (max-width: ${SCREEN_SM}) {
    width: 100%;
    margin-left: 0.24rem;
    align-self: stretch;
  }
`;

const StyledBookNameDiv = styled.div`
  margin-top: 0;
  margin-left: 0;
  padding: 0 0 0 0;

  border-radius: 0;

  text-align: left;

  font-size: 24px;
  line-height: 40px;
  color: #3A2E22;
  text-align: left;
  font-weight: bold;
  font-family: "Noto Serif SC", "Songti SC", "STSong", serif;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  max-width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    font-size: 0.42rem;
    line-height: 0.54rem;
  }
`;

const StyledBookAuthorDiv = styled.div`
  margin-top: 10px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 20px;
  line-height: 30px;
  color: #8A7B64;
  text-align: left;
  font-weight: normal;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.18rem;
    font-size: 0.32rem;
    line-height: 0.44rem;
  }
`;

/* Author + date read as one block, pinned to the bottom of the row on
   mobile so the date baseline lines up with the bottom of the cover. */
const StyledBookMetaDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: auto;
  }
`;

const StyledRecommedTimeDiv = styled.div`
  margin-top: 0px;
  margin-left: 0;
  padding: 0 0 0 0;
  border-radius: 0;

  text-align: left;

  font-size: 20px;
  line-height: 30px;
  color: #A89A80;
  text-align: left;
  font-weight: normal;
  white-space: pre-space;

  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.06rem;
    font-size: 0.3rem;
    line-height: 0.42rem;
  }
`;

const StyledBookTag = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-top: 8px;
  padding: 2px 10px;
  border-radius: 4px;
  background: ${(props) => props.$color + "1A"};
  color: ${(props) => props.$color};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  white-space: nowrap;

  @media (max-width: ${SCREEN_SM}) {
    margin-top: 0.08rem;
    font-size: 0.22rem;
    padding: 1px 0.12rem;
    letter-spacing: 0.5px;
  }
`;

const StyledBookNameTagWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BookNameWithTag: React.FC<{
  name: string;
  tagColor: string;
  tagLabel: string;
}> = ({ name, tagColor, tagLabel }) => {
  return (
    <StyledBookNameTagWrap>
      <StyledBookNameDiv>{name}</StyledBookNameDiv>
      <StyledBookTag $color={tagColor}>{tagLabel}</StyledBookTag>
    </StyledBookNameTagWrap>
  );
};

export interface BSYearPageProps {}
export const BSYearPage: React.FC<BSYearPageProps> = () => {
  const params = useParams();

  const [currentYear, setCurrentYear] = useState<string>("");
  const [activeTag, setActiveTag] = useState<string>("");

  const gotoMain = useCallback(() => {
    navigate(getNavPath(BSMainPage.name));
  }, []);

  useEffect(() => {
    if (params?.year && params.year !== currentYear) {
      setCurrentYear(params.year);
    }
  }, [params, currentYear]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const list = useLoadYearBookList(currentYear);
  const allBooks = useLoadBooklist();

  const navigate = useNavigate();
  const gotoYear = useCallback(
    (year: number) => {
      navigate("/books/" + year);
    },
    [navigate]
  );

  // Extract available years from all book data
  const availableYears = useMemo((): number[] => {
    if (!allBooks || allBooks.length === 0) return [];
    const years = new Set<number>();
    allBooks.forEach((info) => {
      if (!isEmpty(info.date)) {
        const mmt = moment(info.date, "YYYY/MM/DD");
        if (mmt.isValid()) {
          years.add(mmt.year());
        }
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [allBooks]);

  // Build dropdown menu items
  const yearMenuItems: MenuProps["items"] = availableYears.map((year) => ({
    key: String(year),
    label: `${year}年`,
    style: { whiteSpace: "nowrap" },
  }));

  const handleYearMenuClick: MenuProps["onClick"] = ({ key }) => {
    gotoYear(Number(key));
  };

  // Extract available tags from current year book data
  const availableTags = useMemo((): string[] => {
    if (!list || list.length === 0) return [];
    const tagSet = new Set<string>();
    list.forEach((info) => {
      const tag = info.tag || "other";
      tagSet.add(tag);
    });
    return BOOK_TAGS.filter((t) => tagSet.has(t.key)).map((t) => t.key);
  }, [list]);

  // Filter books by active tag
  const filteredList = useMemo((): BSBookInfo[] | undefined => {
    if (!list) return undefined;
    if (!activeTag) return list;
    return list.filter((info) => (info.tag || "other") === activeTag);
  }, [list, activeTag]);

  const [books, setBooks] = useState<BSBookInfoByMonth[]>();

  const groupListByMonth = useCallback(
    (filtered: BSBookInfo[]) => {
      var groups = new Map();

      filtered?.map((info: BSBookInfo) => {
        if (!isEmpty(info.date)) {
          var mmt = moment(info.date, "YYYY/MM/DD");
          info.date_timestamp = mmt.valueOf();
          var key = mmt.set("date", 1).valueOf();

          var infoArray = [];
          if (groups.has(key)) {
            infoArray = groups.get(key);
          }

          infoArray.push(info);

          groups.set(key, infoArray);
        }

        return 0;
      });

      var temp: BSBookInfoByMonth[] = [];
      groups.forEach((value, key, map) => {
        var item: BSBookInfoByMonth = {
          month: moment(key).format("YYYY年MM月"),
          books: value.reverse(),
        };
        temp.push(item);
      });

      setBooks(temp.reverse());
    },
    []
  );

  const gotoDetail = useCallback(
    (isbn: string) => {
      navigate(getNavPath(BSDetailPage.name) + "/" + isbn, { state: { from: "/books/" + (currentYear || (params?.year ?? "")) } });
    },
    [navigate]
  );

  useEffect(() => {
    if (filteredList !== undefined) {
      groupListByMonth(filteredList);
    }
  }, [filteredList, groupListByMonth]);

  const isSMSize = useIsInSMSize();

  return (
    <SytledWrapper>
      <StyledContainerDiv>
        <StyledSectionGroupDiv>
          <StyledBackDiv onClick={gotoMain}>返回</StyledBackDiv>
          <StyledTitleDiv>宝库-{currentYear}</StyledTitleDiv>
          <Dropdown menu={{ items: yearMenuItems, onClick: handleYearMenuClick }} trigger={["hover", "click"]}>
            <StyledMainDiv>更多</StyledMainDiv>
          </Dropdown>
        </StyledSectionGroupDiv>

        {/* Tag filter bar */}
        {list && list.length > 0 && (
        <StyledTagFilterBar>
          <StyledTagChip
            $isAll
            $active={activeTag === ""}
            onClick={() => setActiveTag("")}
          >
            全部
          </StyledTagChip>
          {availableTags.map((tagKey) => {
            const cfg = getTagConfig(tagKey);
            return (
              <StyledTagChip
                key={tagKey}
                $active={activeTag === tagKey}
                $color={cfg.color}
                $bgColor={cfg.bgColor}
                onClick={() => setActiveTag(activeTag === tagKey ? "" : tagKey)}
              >
                {cfg.label}
              </StyledTagChip>
            );
          })}
        </StyledTagFilterBar>
        )}

        {books === undefined && (
          <BookListSkeleton
            rows={6}
            showTagBar={list === undefined}
            showSectionHeader={false}
          />
        )}

        {books !== undefined && books.length > 0 && (
          <StyledListItemDiv>
            {books?.map((booksInMonth) => (
              <React.Fragment key={booksInMonth.month}>
                {booksInMonth.books?.map((info: BSBookInfo) => {
                  const tagCfg = getTagConfig(info.tag);
                  return (
                    <StyledBookItem key={info.isbn} onClick={() => gotoDetail(info.isbn)}>
                      <BookCover
                        src={toCoverProxyPath(info.cover)}
                        alt={info.title}
                      />
                      <StyledBookRight>
                        <BookNameWithTag
                          name={info.title}
                          tagColor={tagCfg.color}
                          tagLabel={tagCfg.label}
                        />
                        <StyledBookMetaDiv>
                          <StyledBookAuthorDiv>
                            {info.author}{" "}
                          </StyledBookAuthorDiv>
                          <StyledRecommedTimeDiv>
                            推荐日期：{info.date}
                          </StyledRecommedTimeDiv>
                        </StyledBookMetaDiv>
                      </StyledBookRight>
                    </StyledBookItem>
                  );
                })}
              </React.Fragment>
            ))}
          </StyledListItemDiv>
        )}

        {list !== undefined && list.length === 0 && currentYear !== "" && (
          <StyledNotFoundDiv>
            <NotDataView
              title="404"
              subTitle="当前年份没有数据"
              detail={"你可以返回主页继续浏览"}
              url="/"
              urlText="主页"
            ></NotDataView>
          </StyledNotFoundDiv>
        )}
      </StyledContainerDiv>

      <Footer></Footer>
    </SytledWrapper>
  );
};
